import React, { useState } from "react";
import styled from "styled-components";
import { Tag, Tooltip, Button, message } from "antd";
import {
  EditOutlined,
  ThunderboltOutlined,
  CloudOutlined,
  SettingOutlined,
  HomeOutlined,
  ToolOutlined,
  BulbOutlined,
  SendOutlined,
} from "@ant-design/icons";

const RepairItem = ({ repair, onEdit, draftAction, onApproveDraft }) => {
  const [approving, setApproving] = useState(false);
  const handleApprove = async (e) => {
    e.stopPropagation();
    if (!draftAction?.id || !onApproveDraft) return;
    setApproving(true);
    try {
      await onApproveDraft(draftAction.id);
      message.success("Vendor notified");
    } catch (err) {
      message.error(err?.message || "Failed to notify vendor");
    } finally {
      setApproving(false);
    }
  };
  const getCategoryIcon = (category) => {
    const baseStyle = { color: "#ffffff", fontSize: 18 };
    const key = (category || "").toString().toLowerCase().trim();
    switch (true) {
      case key === "plumbing" ||
        key.includes("plumb") ||
        key.includes("leak") ||
        key.includes("pipe"):
        return (
          <CategoryIcon>
            <ToolOutlined
              style={{ ...baseStyle, filter: "brightness(0) invert(1)" }}
            />
          </CategoryIcon>
        );
      case key === "electrical" || key.includes("electric"):
        return (
          <CategoryIcon>
            <ThunderboltOutlined
              style={{ ...baseStyle, filter: "brightness(0) invert(1)" }}
            />
          </CategoryIcon>
        );
      case key === "hvac" || key.includes("ac") || key.includes("heat"):
        return (
          <CategoryIcon>
            <CloudOutlined
              style={{ ...baseStyle, filter: "brightness(0) invert(1)" }}
            />
          </CategoryIcon>
        );
      case key === "appliances" ||
        key === "appliance" ||
        key.includes("appliance"):
        return (
          <CategoryIcon>
            <SettingOutlined
              style={{ ...baseStyle, filter: "brightness(0) invert(1)" }}
            />
          </CategoryIcon>
        );
      case key === "structural" ||
        key.includes("roof") ||
        key.includes("drywall") ||
        key.includes("window"):
        return (
          <CategoryIcon>
            <HomeOutlined
              style={{ ...baseStyle, filter: "brightness(0) invert(1)" }}
            />
          </CategoryIcon>
        );
      default:
        return (
          <CategoryIcon>
            <ToolOutlined
              style={{ ...baseStyle, filter: "brightness(0) invert(1)" }}
            />
          </CategoryIcon>
        );
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "success";
      case "in_progress":
        return "processing";
      case "assigned":
        return "processing";
      case "scheduled":
        return "warning";
      case "pending":
        return "default";
      case "open":
        return "error";
      default:
        return "default";
    }
  };

  // Safely convert Firestore Timestamp/Date/string to Date
  const toDate = (value) => {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const completedAt = toDate(repair.completedDate);

  return (
    <RepairItemContainer onClick={() => onEdit(repair)}>
      <RepairAvatar>{getCategoryIcon(repair.category)}</RepairAvatar>
      <RepairInfo>
        <RepairTitle>{repair.title}</RepairTitle>
        <RepairDetails>
          {repair.unitNumber ? (
            <UnitSpan>Unit {repair.unitNumber}</UnitSpan>
          ) : null}
          <Tooltip
            title={repair.description}
            placement="top"
            mouseEnterDelay={0.5}
          >
            <DescriptionSpan>{repair.description}</DescriptionSpan>
          </Tooltip>
        </RepairDetails>
        {repair.status?.toLowerCase() === "completed" && completedAt && (
          <CompletedAt>
            Completed{" "}
            {completedAt.toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </CompletedAt>
        )}
        {draftAction?.vendorName && draftAction?.status === "draft" && (
          <LeoBlock onClick={(e) => e.stopPropagation()}>
            <LeoLabel>
              <BulbOutlined /> Leo&apos;s Prepared Response
            </LeoLabel>
            <LeoText>
              Send {draftAction.vendorName} to fix{" "}
              {draftAction.issueSummary || repair.title}.
            </LeoText>
            <ApproveButton
              type="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={handleApprove}
              loading={approving}
            >
              Approve Leo&apos;s {draftAction.vendorName} Dispatch
            </ApproveButton>
          </LeoBlock>
        )}
      </RepairInfo>
      <RepairActions>
        <Tag color={getStatusColor(repair.status)}>{repair.status}</Tag>
        <EditIcon
          onClick={(e) => {
            e.stopPropagation();
            onEdit(repair);
          }}
        >
          <EditOutlined />
        </EditIcon>
      </RepairActions>
    </RepairItemContainer>
  );
};

// Styled Components
const RepairItemContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;
  overflow: hidden;
  min-height: 112px;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    margin: 0 0.5rem;
    min-height: auto;
  }
`;

const RepairAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
  background: var(--color-primary);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
`;

const RepairInfo = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    min-width: 0;
    flex: 1;
  }
`;

const RepairTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.25rem;
  font-family: var(--font-secondary);
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.2rem;
  }
`;

const RepairDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
  width: 100%;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 0.2rem;
    margin-bottom: 0.5rem;
  }
`;

const RepairActions = styled.div`
  flex-shrink: 0;
  align-items: flex-start;
  gap: 0.5rem;
  display: flex;

  .ant-tag {
    font-size: 0.75rem;
    padding: 4px 12px;
    margin: 0;
    white-space: nowrap;
    font-weight: 600;
    border-radius: 6px;
    color: white !important;
    border: none !important;

    /* Ensure good contrast for different status colors */
    &.ant-tag-success {
      background-color: #52c41a !important;
      color: white !important;
    }

    &.ant-tag-warning {
      background-color: #faad14 !important;
      color: white !important;
    }

    &.ant-tag-error {
      background-color: #ff4d4f !important;
      color: white !important;
    }

    &.ant-tag-processing {
      background-color: #1890ff !important;
      color: white !important;
    }

    &.ant-tag-default {
      background-color: #d9d9d9 !important;
      color: #595959 !important;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;

    .ant-tag {
      font-size: 0.7rem;
      padding: 3px 10px;
    }
  }
`;

const EditIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.2);
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
`;

const CategoryIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const UnitSpan = styled.span`
  font-size: 0.9rem;
  color: var(--color-primary);
  font-weight: 700;
  text-align: left;
  display: block;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const DescriptionSpan = styled.span`
  font-size: 0.95rem;
  color: var(--color-text);
  line-height: 1.5;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 2.85rem; /* 2 lines * 1.5 line-height * 0.95rem font-size */

  @media (max-width: 768px) {
    font-size: 0.8rem;
    max-height: 2.4rem; /* Adjusted for smaller font */
    -webkit-line-clamp: 2;
  }
`;

const LeoBlock = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem 0.6rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  width: 100%;
`;
const LeoLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-primary);
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;
const LeoText = styled.div`
  font-size: 0.85rem;
  color: #166534;
  margin-bottom: 0.35rem;
`;
const ApproveButton = styled(Button)`
  font-size: 0.8rem;
`;

export default RepairItem;

// Additional UI elements
const CompletedAt = styled.div`
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: #16a34a;
  font-weight: 600;
`;
